import LogRocket from 'logrocket';

const MAX_GROUP_DEPTH = 10; // This is a guess and not enforced

/* @ngInject */
export default function GroupService($q, $sails, $log) {
  const service = this;

  service.getGroups = getGroups;
  // currently just returns a big object, eventually will $http and get the user info for their user_id
  service.getGroupTree = getGroupTree;
  service.getUserList = getUserList;
  service.createUser = createUser;
  service.deleteUser = deleteUser;
  service.addNewGroup = addNewGroup;
  service.deleteGroup = deleteGroup;
  service.editGroup = editGroup;
  service.editPermissionLevels = editPermissionLevels;
  service.editGroupParent = editGroupParent;
  service.getGroup = getGroup;
  service.subscribeToGroupBroadcasts = subscribeToGroupBroadcasts;
  service.getGroupByOrg = getGroupByOrg;
  service.findPathTo = findPathTo;

  const pathCache = {};


  /**
   * Get groups as list
   *
   * @param {Array<object>} [tree] If supplied, fetch is skipped and arg is used as tree
   * This is useful to just transform an existing tree into a flat list of groups
   * @returns {Array<object>}
   */
  async function getGroups(tree) {
    // TODO: Remove this null-safety patch
    if (!tree) {
      const fetch = await getGroupTree();
      if (!fetch.data) {
        const err = new Error('Tree retrieved but null');
        LogRocket.captureException(err, { extra: { arg: _.toString(tree), code: fetch.statusCode } });
        throw err;
      }
      ({ data: tree } = fetch);
    }
    const flattenGroups = group => [group, ...group.subGroups.map(flattenGroups)];
    return [...tree.map(flattenGroups)].flat(MAX_GROUP_DEPTH);
  }

  let treeCache;
  /**
   * @param {boolean} renew If true, cache is renewed
   * @returns {Promise<{data?: object, error?: object}>}
   */
  function getGroupTree(renew) {
    if (renew || !treeCache) {
      treeCache = $sails.get('/group/tree')
        .catch((error) => {
          $log.error('Error: ', error);
          return $q.reject(error.data);
        });
    }
    return treeCache;
  }

  function getUserList(userListId) {
    return $sails.get(`/userlist/${userListId}`)
      .catch((error) => {
        $log.error('Error: ', error);
        return $q.reject(error.data);
      });
  }

  function createUser(userData) {
    return $sails.post('/user/', userData)
      .catch((error) => {
        $log.error('Error: ', error);
        return $q.reject(error.data);
      });
  }

  function deleteUser(userId) {
    return $sails.delete(`/user/${userId}`)
      .catch((error) => {
        $log.error('Error: ', error);
        return $q.reject(error.data);
      });
  }

  function addNewGroup(newGroupObject) {
    return $sails.post('/group/', newGroupObject)
      .then(({ data }) => _.assign(data, {
        users: [],
        subGroups: []
      }))
      .catch((error) => {
        $log.error('Error: ', error);
        return $q.reject(error.data);
      });
  }

  function deleteGroup(groupId) {
    return $sails.delete(`/group/${groupId}`)
      .catch((error) => {
        $log.error('Error: ', error);
        return $q.reject(error.data);
      });
  }

  function editGroup(groupId, updatedGroupFields) {
    return $sails.put(`/group/${groupId}`, updatedGroupFields)
      .catch((error) => {
        $log.error('Error: ', error);
        return $q.reject(error.data);
      });
  }

  function editPermissionLevels(groupId, permContainer) {
    // permissions property
    return $sails.put(`/group/${groupId}`, permContainer)
      .catch((error) => {
        $log.error('Error: ', error);
        return $q.reject(error.data);
      });
  }

  function editGroupParent(groupId, newParent) {
    return $sails.put(`/group/${groupId}`, newParent)
      .then((res) => {
        bustCache(groupId);
        return res;
      })
      .catch((error) => {
        $log.error('Error: ', error);
        return $q.reject(error.data);
      });
  }

  function getGroup(groupId) {
    return $sails.get(`/group/${groupId}`)
      .then(({ data }) => data)
      .catch((error) => {
        $log.error('Error: ', error);
        return $q.reject(error.data);
      });
  }

  function subscribeToGroupBroadcasts() {
    return $sails.get('/group/subscribe');
  }

  /**
   * Find path to a given group
   *
   * @param {object | string} group Literal or ID
   * @param {Array<object>} tree
   * @returns {Promise<Array<string>>} IDs from root -> group
   */
  async function findPathTo(group, tree) {
    if (!(group && tree)) {
      throw new TypeError('Missing args');
    }

    const { id = group } = group;
    if (!pathCache[id]) {
      group = _.isString(group) ? await getGroup(group) : group;

      const flattenSubGroups = grp => [grp, ...grp.subGroups.map(flattenSubGroups)];
      const groups = _.keyBy([...tree.map(flattenSubGroups)].flat(MAX_GROUP_DEPTH), 'id');

      let path = [];
      let currentGroup = group;
      do {
        path.push(currentGroup.id);
        currentGroup = groups[currentGroup.parent];
      } while (currentGroup);
      path = _.reverse(path); // [root, ..., target]

      // Store cache for both the target group arg as well as groups of sub-paths
      _.times(path.length, (i) => {
        const subPath = path.slice(0, i + 1);
        pathCache[_.last(subPath)] = subPath;
      });
    }

    return pathCache[id];
  }

  /**
   * Clear cache (when changes to model are invalidating)
   *
   * @private
   * @param {string} __culprit TODO:Unused
   * @returns {undefined}
   */
  function bustCache(__culprit) {
    // TODO: Refine this to selectively bust only *affected paths*. Requires too much testing for now...
    Object.keys(pathCache).forEach(key => delete pathCache[key]);
  }


  function getGroupByOrg(orgId, includeSubOrgGroups) {
    // API is expecting `1` for true
    const qs = includeSubOrgGroups ? '?includeSubOrgGroups=1' : '';
    orgId = orgId?.id || orgId;
    return $sails.get(`/group/byorg/${orgId}${qs}`);
  }
}
